<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  routePlansApi, type RoutePlan, type RoutePlanVisit, type RouteAssignment,
} from '@/api/route-plans';
import { blockedProfessionalPayload, type BlockedProfessionalPayload } from '@/utils/api-errors';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import TimeInput from '@/components/time-input.vue';
import RouteBlockedConfirmDialog from './route-blocked-confirm-dialog.vue';
import { effectivePrioritized } from './route-plan-visit.utils';

// Minimal editor for a single visit: the REQUIREMENT (patient, times, duration, skill) is shown
// read-only; only the placement — which caregiver attends and at what time — is editable.
// Placement is a plain pin: saving never re-optimizes anything.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
  visit: RoutePlanVisit | null;
  assignment: RouteAssignment | null;
  // When opened by clicking an empty calendar slot (no existing assignment): preselect the
  // caregiver of that column and the clicked time.
  prefillCaregiverId?: number | null;
  prefillArrivalTime?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  prefillCaregiverId: null,
  prefillArrivalTime: null,
});
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();

const caregiverId = ref<number | null>(null);
const arrivalTime = ref<string>('');
const busy = ref(false);
const errorText = ref('');
const confirmingRemove = ref(false);
const blockedPrompt = ref<BlockedProfessionalPayload | null>(null);

const planId = computed(() => props.routePlan.id);
const blockedIds = computed(() => new Set(props.visit?.blockedCaregiverIds ?? []));

// Blocked professionals stay selectable but visibly marked: picking one triggers a
// warning + confirm round-trip instead of a hard rejection.
const caregiverOptions = computed(() => props.routePlan.routePlanCaregivers.map((rpc) => ({
  value: rpc.caregiverId,
  title: rpc.caregiverFullName,
  props: blockedIds.value.has(rpc.caregiverId) ?
    { subtitle: t('routePlan.visitForm.blocked'), appendIcon: 'mdi-alert-outline' } :
    {},
})));

function seed() {
  errorText.value = '';
  confirmingRemove.value = false;
  blockedPrompt.value = null;
  if (!props.visit) return;

  if (props.assignment) {
    caregiverId.value = props.assignment.caregiverId;
    arrivalTime.value = props.assignment.humanArrivalTime;
  } else {
    caregiverId.value = props.prefillCaregiverId;
    arrivalTime.value = props.prefillArrivalTime ?? props.visit.fixedStartTime ??
      props.visit.timeWindowStart ?? '';
  }
}

watch(() => props.modelValue, (open) => {
  if (open) seed();
}, { immediate: true });

// The requirement, rendered read-only.
const requirementTime = computed(() => {
  const visit = props.visit;
  if (!visit) return '';
  if (visit.fixedStartTime) return t('routePlan.board.fixedAt', { time: visit.fixedStartTime });
  if (visit.timeWindowStart && visit.timeWindowEnd) {
    return t('routePlan.visitForm.between',
      { start: visit.timeWindowStart, end: visit.timeWindowEnd });
  }

  return t('routePlan.board.noWindow');
});

const canSave = computed(() => !!props.visit &&
  (caregiverId.value === null || !!arrivalTime.value));

function placementError(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: string[] } } })?.response?.data;

  return data?.errors?.length ? data.errors.join(' · ') : t('routePlan.visitForm.saveError');
}

// Place / move / unplace based on the chosen caregiver vs the current placement.
async function applyPlacement(overrideBlocked = false): Promise<string[]> {
  const visit = props.visit!;
  const assignment = props.assignment;

  if (caregiverId.value === null) {
    if (assignment) await routePlansApi.unplaceAssignment(planId.value, assignment.id);

    return [];
  }

  const placement = {
    caregiverId: caregiverId.value,
    arrivalTime: `${props.routePlan.date} ${arrivalTime.value}`,
    overrideBlocked,
  };
  const result = assignment ?
    await routePlansApi.moveAssignment(planId.value, assignment.id, placement) :
    await routePlansApi.placeAssignment(planId.value, visit.id, placement);

  return result.warnings;
}

function handleSaveError(error: unknown) {
  const blocked = blockedProfessionalPayload(error);
  if (blocked) {
    blockedPrompt.value = blocked;
  } else {
    errorText.value = placementError(error);
  }
}

async function onSave(overrideBlocked = false) {
  if (!props.visit || busy.value) return;
  busy.value = true;
  errorText.value = '';

  try {
    const warnings = await applyPlacement(overrideBlocked);
    blockedPrompt.value = null;
    emit('saved', warnings);
    emit('update:modelValue', false);
  } catch (error) {
    handleSaveError(error);
  } finally {
    busy.value = false;
  }
}

async function onRemove() {
  if (!props.visit || busy.value) return;
  busy.value = true;

  try {
    await routePlansApi.destroyVisit(planId.value, props.visit.id);
    emit('saved', []);
    emit('update:modelValue', false);
  } catch (error) {
    errorText.value = placementError(error);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="600"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card
      v-if="visit"
      class="pa-2"
    >
      <v-card-title class="d-flex align-center ga-2">
        <span class="text-truncate">{{ visit.patientFullName }}</span>
        <v-chip
          v-if="visit.serviceCategoryName"
          size="x-small"
          variant="tonal"
          color="primary"
        >
          {{ visit.serviceCategoryName }}
        </v-chip>
        <v-chip
          v-if="effectivePrioritized(visit)"
          size="x-small"
          variant="tonal"
          color="warning"
          prepend-icon="mdi-star"
        >
          {{ t('routePlan.board.prioritized') }}
        </v-chip>
        <v-spacer />
        <v-btn
          v-if="assignment"
          icon
          size="small"
          variant="outlined"
          color="grey-darken-1"
          :disabled="busy"
          :aria-label="t('routePlan.visitForm.unplace')"
          @click="caregiverId = null; onSave()"
        >
          <v-icon>mdi-arrow-u-left-top</v-icon>
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            {{ t('routePlan.visitForm.unplace') }}
          </v-tooltip>
        </v-btn>
        <v-btn
          icon
          size="small"
          variant="outlined"
          color="red"
          :disabled="busy"
          :aria-label="t('routePlan.visitForm.remove')"
          @click="confirmingRemove = !confirmingRemove"
        >
          <v-icon>mdi-delete</v-icon>
          <v-tooltip
            activator="parent"
            location="bottom"
          >
            {{ t('routePlan.visitForm.remove') }}
          </v-tooltip>
        </v-btn>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="t('routePlan.cancel')"
          @click="emit('update:modelValue', false)"
        />
      </v-card-title>

      <v-card-text>
        <!-- The requirement (read-only) -->
        <v-sheet
          class="pa-4 rounded-lg bg-grey-lighten-4"
        >
          <div class="text-subtitle-2 font-weight-medium mb-2">
            {{ t('routePlan.visitForm.requirementTitle') }}
          </div>
          <div
            v-if="visit.patientAddress"
            class="d-flex align-center py-1 text-body-2"
          >
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-map-marker-outline
            </v-icon>
            <span>{{ visit.patientAddress }}</span>
          </div>
          <div class="d-flex align-center py-1 text-body-2">
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-clock-outline
            </v-icon>
            <span>{{ requirementTime }}</span>
          </div>
          <div class="d-flex align-center py-1 text-body-2">
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-timer-sand
            </v-icon>
            <span>{{ visit.durationMinutes }} {{ t('routePlan.minutes') }}</span>
          </div>
          <div
            v-if="visit.skillName"
            class="d-flex align-center py-1 text-body-2"
          >
            <v-icon
              size="18"
              class="mr-3 text-medium-emphasis"
            >
              mdi-star-outline
            </v-icon>
            <span>{{ visit.skillName }}</span>
          </div>
        </v-sheet>

        <!-- The placement (editable) -->
        <v-row class="mt-3">
          <v-col
            cols="12"
            sm="7"
          >
            <v-select
              v-model="caregiverId"
              :items="caregiverOptions"
              :label="t('routePlan.visitForm.caregiver')"
              :placeholder="t('routePlan.visitForm.unassigned')"
              item-props
              clearable
              variant="outlined"
              density="comfortable"
              hide-details="auto"
            />
          </v-col>
          <v-col
            cols="12"
            sm="5"
          >
            <time-input
              v-model="arrivalTime"
              density="comfortable"
              :label="t('routePlan.arrivalTime')"
              :disabled="caregiverId === null"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <template v-if="confirmingRemove">
          <span class="text-body-2 mr-2">{{ t('routePlan.visitForm.removeConfirm') }}</span>
          <v-btn
            color="error"
            variant="flat"
            size="small"
            :loading="busy"
            @click="onRemove"
          >
            {{ t('routePlan.confirm') }}
          </v-btn>
        </template>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canSave"
          @click="onSave()"
        >
          {{ t('routePlan.visitForm.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <route-blocked-confirm-dialog
      :model-value="blockedPrompt !== null"
      :caregiver-name="blockedPrompt?.caregiverName ?? ''"
      :patient-name="blockedPrompt?.patientName ?? ''"
      :busy="busy"
      @confirm="onSave(true)"
      @cancel="blockedPrompt = null"
    />
  </responsive-dialog>
</template>
